import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Form, Input, InputNumber, Select, Button, message, Divider } from 'antd';
import { useTranslation } from 'react-i18next';
import { track } from '../analytics';
import { requestTurnstileToken } from '../utils/turnstile';

const { TextArea } = Input;

const QuoteModal = ({ open, onClose, defaultArea, quoteData, hideArea = false }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      // Obtain human verification token (Cloudflare Turnstile)
      const turnstileToken = await requestTurnstileToken('quote_submit');
      const browserLanguage = navigator.language.split('-')[0];
      const payload = {
        intent: 'quote',
        email: values.email,
        organizationName: values.organizationName,
        ...(hideArea ? {} : { area: values.area }),
        countries: values.countries,
        crops: values.crops,
        role: values.role,
        notes: values.notes,
        language: browserLanguage,
        calculator: quoteData || null,
        turnstileToken,
      };

      const response = await fetch(`${process.env.REACT_APP_API_URL}/request-quote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      await response.json().catch(() => ({}));
      message.success(t('quote.success'));
      try {
        const total = quoteData?.breakdown?.totals?.total ?? null;
        const cropsCount = (values.crops || '').split(',').filter(Boolean).length || (quoteData?.selectedCrops?.length ?? null);
        track('quote_submit_success', {
          area: values.area,
          cropsCount,
          analytics: quoteData?.selectedAnalytics || [],
          total
        });
      } catch {}
      setLoading(false);
      onClose();
      form.resetFields();
    } catch (err) {
      message.error(t('quote.error'));
      try { track('quote_submit_error'); } catch {}
      setLoading(false);
    }
  };

  const initialValues = useMemo(() => ({
    email: '',
    organizationName: '',
    area: hideArea ? undefined : (quoteData?.area ?? defaultArea),
    countries: '', // user should specify
    crops: quoteData?.cropsText || '',
    role: undefined,
    notes: '',
  }), [quoteData, defaultArea, hideArea]);

  useEffect(() => {
    if (open) {
      form.setFieldsValue(initialValues);
    }
  }, [open, initialValues, form]);

  const renderBreakdown = () => {
    if (!quoteData?.breakdown) return null;
    const { lines, totals } = quoteData.breakdown;
    return (
      <div className="mb-6 p-4 rounded-xl border border-gray-200 bg-gradient-to-br from-white to-gray-50">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-lg font-semibold m-0">{t('quote.breakdown')}</h4>
          {quoteData?.selectedRegion && (
            <span className="text-sm text-gray-500">Region: {quoteData.selectedRegion}</span>
          )}
        </div>
        <Divider className="my-3" />
        <div className="space-y-1">
          {lines.map((l, idx) => (
            <div key={idx} className="flex items-center justify-between py-1">
              <span className="text-gray-700">{l.label}</span>
              <span className={`font-medium ${l.amount < 0 ? 'text-green-600' : ''}`}>
                {l.amount < 0 ? '-' : ''}€{Math.abs(l.amount).toLocaleString()}
              </span>
            </div>
          ))}
        </div>
        <Divider className="my-3" />
        <div className="flex items-center justify-between">
          <span className="font-semibold">Total</span>
          <span className="font-bold text-lg">€{(totals?.total ?? 0).toLocaleString()}</span>
        </div>
      </div>
    );
  };

  return (
    <Modal
      title={t('quote.title')}
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      width={560}
    >
      <div className="py-2">
        <p className="text-gray-600 mb-4">{t('quote.intro')}</p>
        {renderBreakdown()}
        <Form form={form} layout="vertical" onFinish={handleSubmit} requiredMark={false} initialValues={initialValues}>
          <Form.Item name="email" label={t('quote.email')} rules={[{ required: true }, { type: 'email' }]}> 
            <Input size="large" placeholder={t('signup.emailPlaceholder')} />
          </Form.Item>
          <Form.Item name="organizationName" label={t('quote.org')} rules={[{ required: true }]}> 
            <Input size="large" placeholder={t('quote.orgPlaceholder', 'Company / Program name')} />
          </Form.Item>
          {!hideArea && (
            <Form.Item name="area" label={t('quote.area')} rules={[{ required: true }]}> 
              <InputNumber className="w-full" size="large" min={1} placeholder={t('quote.areaPlaceholder', 'e.g., 5000')} />
            </Form.Item>
          )}
          <Form.Item name="countries" label={t('quote.countries')} rules={[{ required: true, message: 'Please enter at least one country' }]}> 
            <Input size="large" placeholder={t('quote.countriesPlaceholder', 'e.g., Netherlands, Germany')} />
          </Form.Item>
          <Form.Item name="crops" label={t('quote.crops')} rules={[{ required: true, message: 'Please enter at least one crop' }]}> 
            <Input size="large" placeholder={t('quote.cropsPlaceholder', 'e.g., wheat, corn')} />
          </Form.Item>
          <Form.Item name="role" label={t('quote.role')}>
            <Select size="large" placeholder={t('quote.rolePlaceholder', 'Select your role')} allowClear>
              <Select.Option value="operations">Operations</Select.Option>
              <Select.Option value="agronomy">Agronomy</Select.Option>
              <Select.Option value="procurement">Procurement</Select.Option>
              <Select.Option value="it">IT / Data</Select.Option>
              <Select.Option value="other">Other</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name="notes" label={t('quote.notes')}>
            <TextArea rows={3} placeholder={t('quote.notesPlaceholder', 'Anything else we should know?')} />
          </Form.Item>
          <Form.Item className="mb-0">
            <Button type="primary" htmlType="submit" loading={loading} size="large" className="w-full bg-primary border-primary h-12 text-lg font-semibold">
              {t('quote.submit')}
            </Button>
          </Form.Item>
        </Form>
      </div>
    </Modal>
  );
};

export default QuoteModal;
